import type { CityConfig } from "@/pages/city-template/types";

export const superiorConfig: CityConfig = {
  city: "Superior",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "superior",
  route: "/cities/superior",
  title: "Emergency & Expedited Passport Services Superior WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Superior, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago via overnight shipping. A+ BBB rated. Serving Douglas County, Twin Ports, Duluth MN cross-border residents, and far northwest Wisconsin.",
  h1: "Emergency & Expedited Passport Services in Superior, WI",
  heroSubtitle: "Superior is Wisconsin's northwesternmost city, sharing the Twin Ports with Duluth, Minnesota — about 480 miles from the Chicago Passport Agency. Despite the distance, Fast Passport Center's registered couriers use overnight shipping to deliver your documents to Chicago so you get a new or renewed passport as fast as 24 hours. Whether you're at UW-Superior, working in the Twin Ports, or a Douglas County resident needing urgent travel documents — we've got you covered.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Superior%20Wisconsin%20Lake%20Superior%20shoreline%20at%20golden%20hour%20with%20massive%20ore%20docks%20and%20industrial%20harbor%20silhouette%2C%20vast%20freshwater%20lake%20stretching%20to%20horizon%2C%20warm%20amber%20light%20on%20calm%20deep%20blue%20water%2C%20historic%20port%20infrastructure%20with%20grain%20elevators%2C%20professional%20landscape%20photography%2C%20wide%20angle%20composition%20showing%20harbor%20and%20lake%20expanse%2C%20soft%20natural%20light%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=superior-hero-001&orientation=landscape",
  heroImageAlt: "Superior Wisconsin Lake Superior shoreline and ore docks at golden hour",
  geoPosition: "46.7208;-92.1041",
  geoPlacename: "Superior, Wisconsin",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 480 miles from Superior — overnight shipping required via courier",
  },
  nearbyCitiesZips: [
    { name: "Downtown Superior", zips: "54880" },
    { name: "East Superior", zips: "54880" },
    { name: "South Superior", zips: "54880" },
    { name: "Duluth MN", zips: "55802, 55806, 55807, 55812" },
    { name: "Hermantown MN", zips: "55811" },
    { name: "Proctor MN", zips: "55810" },
    { name: "Cloquet MN", zips: "55720" },
    { name: "Solon Springs", zips: "54873" },
    { name: "Poplar", zips: "54864" },
    { name: "Maple", zips: "54854" },
    { name: "Ashland", zips: "54806" },
    { name: "Hayward", zips: "54843" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Overnight courier delivery to Chicago Passport Agency — 480 miles from Superior" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Twin Ports Coverage", desc: "Serving Superior WI, Duluth MN, and the entire Douglas County border region" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Superior case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Minneapolis MN", route: "/cities/minneapolis" },
    { name: "Expedited passport in St Paul MN", route: "/cities/st-paul" },
    { name: "Urgent passport in Eau Claire WI", route: "/cities/eau-claire" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Chicago passport services", route: "/states/illinois" },
    { name: "Emergency passport in Duluth MN", route: "/cities/duluth-mn" },
    { name: "Expedited passport in Wausau WI", route: "/cities/wausau" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Superior remotely via secure overnight courier. While Superior is the farthest Wisconsin city from Chicago at about 480 miles, our registered couriers use guaranteed overnight shipping to ensure your documents reach the Chicago Passport Agency on schedule. This makes passport processing possible for even the most remote Wisconsin communities.",
  fpcOfficeDistance: "Serving all of Douglas County and the Twin Ports region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Superior, the Twin Ports region, and far northwest Wisconsin. From Lake Superior's south shore to the Minnesota border communities — we ensure Douglas County travelers can get passports as fast as any Wisconsin resident, despite the distance from Chicago.",
  nearbyCitiesIntro: "We serve Superior and nearby communities including Duluth MN, Hermantown, Cloquet, Solon Springs, Ashland, and the entire Twin Ports and Douglas County region.",
  howItWorksIntro: "Most Superior travelers complete everything online and ship documents via prepaid overnight label. At 480 miles from Chicago — Wisconsin's farthest point from the agency — Fast Passport Center's guaranteed overnight courier service ensures your documents arrive on schedule. UW-Superior students and Twin Ports professionals rely on us for fast turnaround despite the distance.",
  mistakesIntro: "Superior travelers, including cross-border Twin Ports residents and Lake Superior region travelers, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Superior and Twin Ports travelers trust us when international travel can't wait. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Superior? Here are the most common questions from Douglas County travelers — especially about the distance from Chicago.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Guaranteed overnight shipping to Chicago",
        "No drive needed — 480 miles would take 7+ hours each way",
        "Ideal for UW-Superior study abroad students",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from DLH or MSP",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "480 miles to Chicago — not driveable for appointment",
        "Overnight courier is the only practical option",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Superior is 480 miles from Chicago — the farthest WI city from the agency. Our overnight courier makes 24-hour processing possible anywhere in Wisconsin.",
      bullets: ["Guaranteed overnight courier to Chicago", "24–48 hour turnaround available", "480 miles covered — no drive needed"],
      badge: "Remote WI Coverage",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Superior adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Overnight courier options", "Serving Superior and Duluth metro"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Superior and Douglas County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Superior, Duluth, and Douglas County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago Agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Douglas County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Superior and Twin Ports residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Superior WI", description: "Urgent passport processing for Superior, WI travelers via Chicago Passport Agency overnight courier — about 480 miles from Superior, Wisconsin's farthest point from the agency." },
    { name: "Passport Renewal Superior WI", description: "Fast passport renewal for Superior and Douglas County, Wisconsin residents." },
    { name: "New Passport Superior WI", description: "First-time passport application assistance for Superior and far northwest Wisconsin residents." },
    { name: "Lost Passport Superior WI", description: "Passport replacement for lost or stolen passports for Superior and Twin Ports residents." },
    { name: "Child Passport Superior WI", description: "Minor passport services for Superior and Douglas County families." },
    { name: "Name Change Passport Superior WI", description: "Passport name change service for Superior, WI residents." },
  ],
  testimonials: [
    {
      name: "Erik J.",
      location: "Downtown Superior, WI",
      rating: 5,
      text: "I was worried being so far from Chicago — Superior is basically on the other side of Wisconsin. But Fast Passport Center's overnight courier handled everything. Got my passport in 4 business days for a business trip to Toronto. Distance really doesn't matter with their system.",
      service: "Expedited Renewal",
    },
    {
      name: "Kaitlyn R.",
      location: "UW-Superior Student",
      rating: 5,
      text: "Study abroad trip to Norway — realized 10 days before departure my passport had expired. Being at UW-Superior, I thought the distance from Chicago would be a problem. Fast Passport Center proved me wrong — overnight courier got it done in 3 days.",
      service: "Emergency Passport",
    },
    {
      name: "Dave M.",
      location: "Duluth, MN (Twin Ports)",
      rating: 5,
      text: "Live in Duluth but work in Superior. Cross-border resident needing a passport for a cruise from Seattle. Fast Passport Center had my renewal back in 5 business days despite being the farthest point from Chicago in the entire region.",
      service: "Expedited Renewal",
    },
    {
      name: "Megan T.",
      location: "Solon Springs, WI",
      rating: 5,
      text: "First passport ever — for a family trip to Ireland. Fast Passport Center guided me through every step, found me an acceptance facility in Superior, and got my passport back in 7 business days. Amazing service for rural Wisconsin.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "sup-1",
      name: "Superior Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "805 Belknap St",
      city: "Superior",
      state: "WI",
      zip: "54880",
      phone: "(715) 394-8240",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Downtown Superior",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Superior. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sup-2",
      name: "Douglas County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1313 Belknap St, Room 201",
      city: "Superior",
      state: "WI",
      zip: "54880",
      phone: "(715) 395-1537",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Downtown Superior",
      website: "https://www.douglascountywi.org/departments/county-clerk",
      websiteLabel: "Douglas County Clerk",
      notes: "Accepts passport applications for Douglas County residents. Convenient downtown location. Photo services not available — bring photos.",
      icon: "ri-government-line",
    },
    {
      id: "sup-3",
      name: "Duluth Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2800 W Michigan St",
      city: "Duluth",
      state: "MN",
      zip: "55806",
      phone: "(218) 722-2300",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "5 miles across the bridge in Duluth MN",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient alternative for Superior and Twin Ports residents on both sides of the border. Larger facility with more appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "sup-4",
      name: "Duluth City Hall — Passport Services",
      type: "Municipal Passport Acceptance Facility",
      address: "411 W 1st St, Room 130",
      city: "Duluth",
      state: "MN",
      zip: "55802",
      phone: "(218) 730-5500",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "5 miles across the bridge in downtown Duluth",
      website: "https://www.duluthmn.gov/",
      websiteLabel: "City of Duluth",
      notes: "City of Duluth municipal passport acceptance. Convenient for cross-border Superior-Duluth residents. Appointment recommended.",
      icon: "ri-building-line",
    },
    {
      id: "sup-5",
      name: "Cloquet Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "316 Cloquet Ave",
      city: "Cloquet",
      state: "MN",
      zip: "55720",
      phone: "(218) 879-2151",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "20 miles southwest of Superior",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Cloquet and southern Douglas County border residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sup-6",
      name: "Ashland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "411 Chapple Ave",
      city: "Ashland",
      state: "WI",
      zip: "54806",
      phone: "(715) 682-2924",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "60 miles east of Superior",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for eastern Douglas County and Ashland/Bayfield County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "480 miles — 7+ hour drive each way, appointment needed", fpc: "We overnight courier — no drive needed at all" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (extremely rare for remote WI)", fpc: "24–48 hours via overnight courier" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking Distance from Chicago Is a Dealbreaker",
      description: "Superior is the farthest Wisconsin city from Chicago — 480 miles away. But overnight courier service eliminates the distance problem entirely. Your documents arrive in Chicago the next morning, same as any Milwaukee or Racine submission.",
    },
    {
      icon: "ri-image-line",
      title: "Getting Photos Across the Border",
      description: "Twin Ports residents often get passport photos at photo shops in Duluth MN. Make sure photos meet U.S. passport specifications — 2x2 inches with a white background — regardless of which side of the bridge you get them on.",
    },
    {
      icon: "ri-file-list-line",
      title: "Confusing Wisconsin and Minnesota Acceptance Facilities",
      description: "Superior residents sometimes try to submit at Duluth MN acceptance facilities with Wisconsin documents. While generally accepted, it's simpler to use a Wisconsin facility. The Douglas County Clerk handles Superior applications with Wisconsin-specific processing.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Accounting for Shipping Time",
      description: "Overnight courier from Superior to Chicago is fast, but you still need to factor in document preparation and return shipping. Start the process at least 7–10 days before your absolute deadline to account for all logistics.",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport Logistics for Rural Wisconsin",
      description: "Minors under 16 require both parents at the acceptance facility. For Superior and Douglas County families, this often means coordinating schedules around limited acceptance facility hours. Plan ahead for rural facility availability.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Standard Processing from Remote Wisconsin",
      description: "Standard USPS processing from Superior takes 8–11 weeks — same as everywhere else, but shipping delays can add extra days. For far northern Wisconsin, always use expedited service for any travel within 10 weeks.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Overnight", description: "We send a prepaid overnight label. Drop at any UPS location in Superior or Duluth — your package arrives in Chicago by 10:30 AM." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Chicago Agency via guaranteed overnight delivery." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots — distance doesn't slow things down." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Superior or Twin Ports address — fully trackable from Chicago to Lake Superior." },
  ],
  faqs: [
    {
      q: "How far is Superior from the Chicago Passport Agency?",
      a: "Superior is approximately 480 miles from the Chicago Passport Agency — the farthest of any Wisconsin city. A drive would take 7+ hours each way. However, Fast Passport Center uses guaranteed overnight courier shipping, so your documents arrive in Chicago by 10:30 AM the next business day, regardless of distance.",
    },
    {
      q: "Does the distance from Chicago slow down processing from Superior?",
      a: "No. While Superior is Wisconsin's farthest city from Chicago at 480 miles, our overnight courier service eliminates the distance problem. Documents shipped from Superior arrive in Chicago just as fast as documents from Milwaukee or Racine. Processing times (2–9 business days standard, 24–48 hours emergency) are the same for all Wisconsin locations.",
    },
    {
      q: "How fast can I get an emergency passport in Superior?",
      a: "Emergency processing is 24–48 hours with overnight courier door-to-door service. Despite the 480-mile distance, our courier system ensures your documents reach Chicago the next morning — same timeline as cities much closer to the agency.",
    },
    {
      q: "Can UW-Superior students get fast passports for study abroad?",
      a: "Yes. UW-Superior students are some of our most frequent customers in far northwest Wisconsin. Study abroad programs often require passports with short notice. Standard expedited processing is 2–9 business days. Start early — at least 8 weeks before departure.",
    },
    {
      q: "Where do Superior travelers fly internationally?",
      a: "Superior travelers typically fly internationally through Duluth International Airport (DLH, 15 miles), Minneapolis-St. Paul (MSP, 160 miles south), or Chicago O'Hare (ORD, 480 miles). DLH offers connections to major hubs via Delta, United, and American. A valid U.S. passport is required for all international flights.",
    },
    {
      q: "Do I need a passport if I'm just going to Canada from Superior?",
      a: "Yes. Superior is very close to the Canadian border — Thunder Bay, Ontario is just across Lake Superior. While land border crossings to Canada previously required only a driver's license, current regulations require a passport book, passport card, or enhanced driver's license. For air travel to Canada, you must have a passport book.",
    },
    {
      q: "How do I renew my passport in Superior?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's overnight courier service achieves 2–9 business day turnaround — even from Superior's remote location.",
    },
    {
      q: "What documents do I need for a first-time passport in Superior?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Superior Post Office or Douglas County Clerk.",
    },
    {
      q: "Can I use Duluth MN passport acceptance facilities as a Wisconsin resident?",
      a: "Generally yes. The Duluth Main Post Office and Duluth City Hall accept passport applications regardless of state residency. However, it's typically simpler to use a Wisconsin acceptance facility like the Superior Post Office or Douglas County Clerk to avoid any jurisdictional confusion.",
    },
    {
      q: "Is a passport card enough for Twin Ports border crossings?",
      a: "A passport card is valid for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean — which works for driving across the border. But for air travel anywhere internationally, you need a full passport book. Superior-Duluth cross-border commuters should get a passport book if there's any chance of flying internationally.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Superior", address: "2202 E 2nd St, Superior, WI 54880", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Superior", address: "2202 E 2nd St, Superior, WI 54880", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Duluth", address: "1020 Junction Ave, Duluth, MN 55812", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Duluth", address: "1315 E Calvary Rd, Duluth, MN 55811", hours: "Mon–Fri: 8AM–6PM, Sat: 10AM–5PM" },
    { name: "UPS Store — Superior", address: "2412 Tower Ave, Superior, WI 54880", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
    { name: "UPS Store — Duluth", address: "1020 Junction Ave, Duluth, MN 55811", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Superior", address: "1705 N 16th St, Superior, WI 54880", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Douglas County residents.", featured: true },
    { name: "MN DVS — Duluth", address: "320 W 2nd St, Suite 101, Duluth, MN 55802", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Minnesota DVS for cross-border Duluth commuters. Note: WI residents should use WI DMV for license renewals.", featured: false },
    { name: "WI DMV — Ashland", address: "201 W Main St, Ashland, WI 54806", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Ashland, Bayfield, and eastern Douglas County residents.", featured: false },
  ],
  airports: [
    {
      code: "DLH",
      name: "Duluth International Airport",
      distance: "15 miles north of Superior",
      address: "4701 Grinden Dr, Duluth, MN 55811",
      note: "Superior's primary commercial airport serving the Twin Ports region. Connections to major hubs via Delta, United, and American. About 20 minutes north across the bridge. Passport required for all international connections.",
      flightUrl: "https://www.duluthairport.com/",
      flightLabel: "DLH Airport",
      featured: true,
    },
    {
      code: "MSP",
      name: "Minneapolis-St. Paul International Airport",
      distance: "160 miles south of Superior",
      address: "4300 Glumack Dr, St. Paul, MN 55111",
      note: "Nearest major international hub for Superior travelers. About 2.5 hours south via I-35. Extensive domestic and international routes via all major carriers.",
      flightUrl: "https://www.mspairport.com/",
      flightLabel: "MSP Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "480 miles southeast of Superior",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Distant but connected via flights from DLH. Co-located with the Chicago Passport Agency. Not practical for driving — use connecting flights from DLH or MSP.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
  ],
};